import { Link, useNavigate } from '@tanstack/react-router';

import { Button, DropdownMenu } from '@keboola/design';
import { ChevronDown, CircleUser, LogOut, ShieldUser, TriangleAlert } from '@keboola/design/icons';

import { useAuth } from '../auth/context';

import { buildAccountMenu, type Section } from './accountMenuModel';

/**
 * The navbar account dropdown.
 *
 * - Names who — or which vendor — you are acting as, and switches between the vendors you belong to.
 * - Carries the vendor ⇄ admin switch, the profile link and sign-out.
 * - Every rule about *what* is offered lives in `buildAccountMenu`; this renders the answer.
 */
export function AccountMenu({ section }: { section: Section }) {
  const { identity, currentVendor, setCurrentVendor, signOut } = useAuth();
  const navigate = useNavigate();

  const menu = buildAccountMenu({
    section,
    vendors: identity?.vendors ?? [],
    currentVendor,
    isAdmin: identity?.isAdmin ?? false,
    name: identity?.name ?? null,
    email: identity?.email ?? null,
  });

  /**
   * Selecting a vendor means "act as this vendor".
   *
   * - From the admin panel, it navigates to the vendor dashboard.
   * - Inside the vendor dashboard, re-scoping alone re-renders the route.
   */
  const selectVendor = (vendorId: string) => {
    setCurrentVendor(vendorId);
    if (section === 'admin') void navigate({ to: '/~' });
  };

  return (
    <DropdownMenu>
      <DropdownMenu.Trigger asChild>
        <Button variant="invisible" size="small">
          {menu.trigger}
          <ChevronDown />
        </Button>
      </DropdownMenu.Trigger>

      <DropdownMenu.Content align="end" className="tw:min-w-56">
        {menu.elevated && (
          // Reports state, not an action: elevated rights should be visible, not inferred from the URL.
          // ⚠️ `aria-disabled` + prevented `onSelect`, not Radix `disabled`, which drops the row from
          // keyboard navigation. `itemVariants` mutes `aria-disabled` rows.
          <>
            <DropdownMenu.Item
              aria-disabled
              icon={<TriangleAlert />}
              onSelect={(event) => event.preventDefault()}
            >
              Using as admin
            </DropdownMenu.Item>
            <DropdownMenu.Separator />
          </>
        )}

        {menu.vendors.length > 0 && (
          <>
            <DropdownMenu.Label>{menu.vendorLabel}</DropdownMenu.Label>
            {/* Radio: exactly one vendor is active, and `RadioGroup` announces that. `value=""` (the admin
                panel) leaves every row unticked. */}
            <DropdownMenu.RadioGroup value={menu.selectedVendor ?? ''} onValueChange={selectVendor}>
              {menu.vendors.map((vendorId) => (
                <DropdownMenu.RadioItem
                  key={vendorId}
                  value={vendorId}
                  indicator="check"
                  indicatorAlign="end"
                  closeOnSelect
                >
                  {vendorId}
                </DropdownMenu.RadioItem>
              ))}
            </DropdownMenu.RadioGroup>
            <DropdownMenu.Separator />
          </>
        )}

        {menu.switchToAdmin && (
          <>
            <DropdownMenu.Item asChild icon={<ShieldUser />}>
              <Link to="/admin">Switch to admin</Link>
            </DropdownMenu.Item>
            <DropdownMenu.Separator />
          </>
        )}

        <DropdownMenu.Item asChild icon={<CircleUser />}>
          <Link to={menu.profileTo}>Profile</Link>
        </DropdownMenu.Item>
        <DropdownMenu.Item icon={<LogOut />} onSelect={() => void signOut()}>
          Sign out
        </DropdownMenu.Item>
      </DropdownMenu.Content>
    </DropdownMenu>
  );
}
